<template>
  <div class="container container-fixed-lg p-t-20">
    <div class=" no-padding p-t-20 p-b-20 bg-white">
      <div class="container">
        <form @submit.prevent="submit">
          <h1>{{ $t('EditProfile.title') }}</h1>

          <div role="alert" class="m-t-20 profile-tip alert alert-warning"
            v-if="userEditing.role == 'recruiter' || userEditing.role == 'candidate'">
            <span class="profile-tip-child">
              {{ $t('EditProfile.tip') }}
            </span>
          </div>

          <h3 class="m-t-20">{{ $t('EditProfile.information_personal') }}
            <i class="fa fa-pencil m-l-10 text-primary hidden" @click="enableEdit('editPersonal')"></i>
          </h3>
          <div class="row m-t-20">
            <label class="col-md-3 control-label">{{ $t('models.user.last_name') }}:</label>
            <div class="col-md-6">
              <div v-if="editPersonal">
                <input name="last_name" type="text" class="form-control" :class="{ 'error': errors.has('last_name') }"
                       :placeholder="$t('models.user.last_name')"
                       v-validate="'required'" :data-vv-as="$t('models.user.last_name')"
                       v-model="userEditing.last_name">
                <span v-show="errors.has('last_name')" class="error">{{ errors.first('last_name') }}</span>
              </div>
              <p v-else>{{ currentUser.last_name }}</p>
            </div>
          </div>
          <div class="row m-t-20">
            <label class="col-md-3 control-label">{{ $t('models.user.first_name') }}:</label>
            <div class="col-md-6">
              <div v-if="editPersonal">
                <input name="first_name" type="text" class="form-control" :class="{ 'error': errors.has('first_name') }"
                       :placeholder="$t('models.user.first_name')"
                       v-validate="'required'" :data-vv-as="$t('models.user.first_name')"
                       v-model="userEditing.first_name">
                <span v-show="errors.has('first_name')" class="error">{{ errors.first('first_name') }}</span>
              </div>
              <p v-else>{{ currentUser.first_name }}</p>
            </div>
          </div>
          <div class="row m-t-20">
            <label class="col-md-3 control-label">{{ $t('models.user.email') }}:</label>
            <div class="col-md-6">
              <span>{{ currentUser.email }}</span>
            </div>
          </div>
          <div class="row m-t-20">
            <label class="col-md-3 control-label">{{ $t('models.user.phone') }}:</label>
            <div class="col-md-6">
              <div v-if="editPersonal">
                <input name="phone" type="text" class="form-control" :class="{ 'error': errors.has('phone') }"
                       :placeholder="$t('models.user.phone')"
                       v-validate="'required'" :data-vv-as="$t('models.user.phone')" v-model="userEditing.phone">
                <span v-show="errors.has('phone')" class="error">{{ errors.first('phone') }}</span>
              </div>
              <p v-else>{{ currentUser.phone_display }}</p>
            </div>
          </div>
          <div class="row m-t-20" v-show="currentUser.home_phone_display">
            <label class="col-md-3 control-label">{{ $t('models.user.home_phone') }}:</label>
            <div class="col-md-6">
              <div v-if="editPersonal">
                <input name="home_phone" type="text" class="form-control" :class="{ 'error': errors.has('home_phone') }"
                       :placeholder="$t('models.user.home_phone')"
                       v-validate="'required'" :data-vv-as="$t('models.user.home_phone')"
                       v-model="userEditing.home_phone">
                <span v-show="errors.has('home_phone')" class="error">{{ errors.first('home_phone') }}</span>
              </div>
              <p v-else>{{ currentUser.home_phone_display }}</p>
            </div>
          </div>
          <hr/>

          <h3 class="m-t-30">{{ $t('EditProfile.change_password_title') }}</h3>

          <div class="row m-t-20">
            <label class="col-md-3 control-label">{{ $t('EditProfile.old_password') }}:</label>
            <div class="col-md-6">
              <input name="old_password" type="password" class="form-control"
                     :class="{ 'error': errors.has('old_password') }" :placeholder="$t('EditProfile.old_password')"
                     v-validate="`${userEditing.password ? 'required|min:8' : ''}`"
                     :data-vv-as="$t('EditProfile.old_password')" v-model="userEditing.old_password">
              <span v-show="errors.has('old_password')" class="error">{{ errors.first('old_password') }}</span>
            </div>
          </div>
          <div class="row m-t-20">
            <div class="col-md-3">
              <label class="control-label m-b-0">{{ $t('models.user.password') }}:</label>
              <p class="help m-b-0"><i>({{ $t('EditProfile.password_note') }})</i></p>
            </div>
            <div class="col-md-6">
              <input name="password" type="password" class="form-control" :class="{ 'error': errors.has('password') }"
                     :placeholder="$t('models.user.password')"
                     v-validate="'min:8'" :data-vv-as="$t('models.user.password')" v-model="userEditing.password">
              <span v-show="errors.has('password')" class="error">{{ errors.first('password') }}</span>
            </div>
          </div>
          <div class="row m-t-20">
            <label class="col-md-3 control-label">{{ $t('EditProfile.password_confirmation') }}:</label>
            <div class="col-md-6">
              <input name="password_confirmation" type="password" class="form-control"
                     :class="{ 'error': errors.has('password_confirmation') }"
                     :placeholder="$t('EditProfile.password_confirmation')"
                     v-validate="`${userEditing.password ? 'required' : ''}`"
                     :data-vv-as="$t('EditProfile.password_confirmation')" v-model="userEditing.password_confirmation">
              <span v-show="errors.has('password_confirmation')" class="error">{{ errors.first('password_confirmation') }}</span>
            </div>
          </div>

          <div class="m-t-40">
            <hr/>

            <h3 class="m-t-30"> Notifications </h3>

            <div class="row m-t-20">
              <div class="col-md-6" v-show="entityAcceptSms">
                <p>{{ $t('EditProfile.notificationSms') }}
                  <span class="switchery switchery-small" :class="acceptSms ? 'switchery-on' : 'switchery-off'" @click.prevent="switchAcceptSms">
                    <small></small>
                  </span>
                </p>
              </div>
              <user-notice-preferences :userEditing="userEditing" :entityAcceptSms="entityAcceptSms"/>
            </div>
          </div>

          <div class="align-items-center">
            <router-link :to="{ name:'Dashboard' }" class="btn btn-default m-t-20 btn-cons">{{ $t("actions.cancel") }}
            </router-link>
            <button type="submit" class="btn btn-primary m-t-20 btn-cons">{{ $t('actions.save') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script type='text/javascript'>
  import { mapActions, mapGetters } from 'vuex'
  import { checkRoleManager } from '@/helpers/roles'
  import UserNoticePreferences from './UserNoticePreferences'

  export default {
    name: 'EditProfile',
    components: {
      UserNoticePreferences
    },
    data() {
      return {
        editPersonal: false,
        editProfessional: false,
        acceptSms: false
      }
    },
    methods: {
      ...mapActions({
        updateProfile: 'users/updateProfile',
      }),
      enableEdit(mode){
        this[mode] = true
      },
      submit(){
        this.$validator.validateAll().then((result) => {
          if(result) {
            var userEditing            = this.userEditing
            userEditing.service_ids    = this.pluckAttribute(userEditing.services, 'id')
            if(!this.isManager) {
              userEditing.occupation_ids = this.pluckAttribute(userEditing.occupations, 'id')
            }
            this.updateProfile(userEditing).then((response) => {
              if(response.status == 200) {
                var message = { type: 'success', content: this.$i18n.t('messages.update_success', { 'resource': this.$i18n.t('resources.user_profile') }) }
                this.$store.dispatch('message/enable', message)
              }
            })
          }
        })
      },
      pluckAttribute(array, attribute){
        return array.map((element) => { return element[attribute] })
      },
      switchAcceptSms(){
        this.acceptSms = !this.acceptSms
      },
      updateAcceptSmsAttr() {
        this.acceptSms = this.userEditing.user_preference_attributes ? this.userEditing.user_preference_attributes.accept_sms : false
      }
    },
    computed: {
      ...mapGetters({
        currentUser: 'auth/currentUser'
      }),
      userEditing() {
        return Object.assign({}, this.currentUser)
      },
      entityAcceptSms(){
        if (this.currentUser.entities) {
          for (let entity of this.currentUser.entities) {
            if (entity.accept_sms) return true
          }
          return false
        }
      },
      isManager() {
        return checkRoleManager(this.currentUser.role)
      }
    },
    watch: {
      currentUser(){
        this.updateAcceptSmsAttr()
      },
      acceptSms() {
        this.userEditing.user_preference_attributes.accept_sms = this.acceptSms
      }
    },
    mounted() {
      this.updateAcceptSmsAttr()
    }
  }
</script>

<style type="text/css">
  .profile-tip {
    text-align: center;
    display: table;
  }
  .profile-tip-child {
    display: table-row;
  }
</style>



// WEBPACK FOOTER //
// src/components/users/EditProfile.vue